import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  templateUrl: './auth-callback.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './auth-callback.component.css',
})
export class AuthCallbackComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private logger = inject(LoggerService).getLogger('auth');

  error = false;
  message = 'Completing sign in...';

  async ngOnInit(): Promise<void> {
    this.logger.debug('🔐 [AUTH CALLBACK] Component initialized');

    // Log full window location to see what URL browser actually received
    this.logger.debug('🌐 [AUTH CALLBACK] Full URL:', window.location.href);
    this.logger.debug('🌐 [AUTH CALLBACK] Search params:', window.location.search);
    this.logger.debug('🌐 [AUTH CALLBACK] Hash:', window.location.hash);

    // Get token from query params
    const token = this.route.snapshot.queryParamMap.get('token');
    const errorParam = this.route.snapshot.queryParamMap.get('error');

    this.logger.debug('🔍 [AUTH CALLBACK] Query params:', {
      hasToken: !!token,
      tokenLength: token?.length,
      error: errorParam,
    });

    if (errorParam) {
      this.logger.error('❌ [AUTH CALLBACK] Error in query params:', errorParam);
      this.error = true;
      this.message = 'Authentication failed. Please try again.';
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 3000);
      return;
    }

    if (token) {
      this.logger.debug('✅ [AUTH CALLBACK] Token received, calling handleCallback...');
      try {
        await this.authService.handleCallback(token);
        this.logger.debug('✅ [AUTH CALLBACK] handleCallback completed successfully');
        // AuthService will navigate to home
      } catch (error) {
        this.logger.error('❌ [AUTH CALLBACK] Error in handleCallback:', error);
        this.logger.error('❌ [AUTH CALLBACK] Error details:', {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        this.error = true;
        this.message = 'Failed to complete sign in. Please try again.';
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
      }
    } else {
      this.logger.error('❌ [AUTH CALLBACK] No token in query params');
      this.error = true;
      this.message = 'No authentication token received.';
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 3000);
    }
  }
}
